import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { FolderPlus } from 'lucide-react';
import { projectsApi } from '../../../api/projectsApi';
import { BuildingSummary } from '../../../types/protocols';

interface NewProjectModalProps {
    show: boolean;
    onHide: () => void;
    /** When set, the project is created in this building and no picker is shown. */
    buildingKey?: string;
    /** Buildings offered by the picker when `buildingKey` is absent. */
    buildings?: BuildingSummary[];
    onCreated: (projectKey: string, buildingKey: string) => void;
}

/**
 * "Nytt projekt": title + dates, and a building picker on the global board
 * where the URL carries no building.
 */
const NewProjectModal: React.FC<NewProjectModalProps> = ({
    show,
    onHide,
    buildingKey,
    buildings,
    onCreated
}) => {
    const [selectedBuildingKey, setSelectedBuildingKey] = useState('');
    const [title, setTitle] = useState('');
    const [startDate, setStartDate] = useState('');
    const [plannedEndDate, setPlannedEndDate] = useState('');
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Reset the form each time the modal opens
    useEffect(() => {
        if (show) {
            setSelectedBuildingKey('');
            setTitle('');
            setStartDate('');
            setPlannedEndDate('');
            setError(null);
        }
    }, [show]);

    const sortedBuildings = useMemo(() =>
        [...(buildings ?? [])]
            .filter(b => !!b.key)
            .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'sv')),
        [buildings]
    );

    const targetBuildingKey = buildingKey ?? selectedBuildingKey;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!targetBuildingKey) {
            setError('Välj en byggnad');
            return;
        }
        if (!title.trim()) {
            setError('Titel krävs');
            return;
        }
        if (!startDate) {
            setError('Startdatum krävs');
            return;
        }
        if (!plannedEndDate) {
            setError('Planerat slutdatum krävs');
            return;
        }
        if (plannedEndDate < startDate) {
            setError('Planerat slutdatum måste vara efter startdatum');
            return;
        }

        try {
            setCreating(true);
            setError(null);
            const result = await projectsApi.createProject(targetBuildingKey, title.trim(), startDate, plannedEndDate);
            if (!result.success || !result.projectKey) {
                setError(result.error || 'Fel vid skapande av projekt');
                return;
            }
            onCreated(result.projectKey, targetBuildingKey);
        } catch (err) {
            console.error('Error creating project:', err);
            setError(err instanceof Error ? err.message : 'Fel vid skapande av projekt');
        } finally {
            setCreating(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Nytt projekt</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
                            {error}
                        </Alert>
                    )}
                    {!buildingKey && (
                        <Form.Group className="mb-3">
                            <Form.Label>Byggnad *</Form.Label>
                            <Form.Select
                                value={selectedBuildingKey}
                                onChange={(e) => setSelectedBuildingKey(e.target.value)}
                                required
                                autoFocus
                            >
                                <option value="">Välj byggnad…</option>
                                {sortedBuildings.map(b => (
                                    <option key={b.key} value={b.key}>{b.name ?? b.key}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    )}
                    <Form.Group className="mb-3">
                        <Form.Label>Titel *</Form.Label>
                        <Form.Control
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="Projekttitel"
                            required
                            autoFocus={!!buildingKey}
                        />
                    </Form.Group>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                        <Form.Group>
                            <Form.Label>Startdatum *</Form.Label>
                            <Form.Control
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Planerat slutdatum *</Form.Label>
                            <Form.Control
                                type="date"
                                value={plannedEndDate}
                                onChange={(e) => setPlannedEndDate(e.target.value)}
                                required
                                min={startDate}
                            />
                        </Form.Group>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide} disabled={creating}>
                        Avbryt
                    </Button>
                    <Button variant="primary" type="submit" disabled={creating}>
                        <FolderPlus size={18} className="me-1" />
                        {creating ? 'Skapar...' : 'Skapa projekt'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default NewProjectModal;
